/* istanbul ignore file - will be replaced during rebranding */
import { faBan, faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FileUploadStatus } from 'src/hooks/useUploadFiles';
import { formatRemainingTime } from './UploadProgressModal';

type Props = Readonly<{
  name: string;
  fileUploadStatus: FileUploadStatus | undefined;
  fullWidth?: boolean;
  showPercentage?: boolean;
  showIcon?: boolean;
  largeText?: boolean;
}>;

export function ProgressBar({
  name,
  fileUploadStatus,
  fullWidth = false,
  showPercentage = false,
  showIcon = true,
  largeText = false,
}: Props) {
  const { t } = useTranslation('uploads');

  /* istanbul ignore next */
  if (!fileUploadStatus) return null;
  const { percentage, isSuccess, isError, remainingTime } = fileUploadStatus;

  let statusIcon = <FontAwesomeIcon icon={faBan} />;

  /* istanbul ignore next */
  if (isError) {
    statusIcon = <FontAwesomeIcon icon={faXmark} style={{ color: 'red' }} />;
  }

  if (isSuccess) {
    statusIcon = <FontAwesomeIcon icon={faCheck} style={{ color: 'green' }} />;
  }

  let statusText: string =
    remainingTime === undefined
      ? t('uploading')
      : /* istanbul ignore next */
        t('uploading', {
          context: 'withTimeLeft',
          time: formatRemainingTime(remainingTime),
        });

  /* istanbul ignore next */
  if (isError) {
    statusText = t('status.error.somethingWentWrongDuringFileUpload');
  }

  if (isSuccess) {
    statusText = t('status.success.fileUploadSuccessfully');
  }

  return (
    <Container data-testid="file-progress-row" fullWidth={fullWidth}>
      <Grid>
        {showIcon && <StatusIcon>{statusIcon}</StatusIcon>}
        <FileName isError={isError} largeText={largeText}>
          <span>{name}</span>
        </FileName>
        <Status largeText={largeText}>{statusText}</Status>
      </Grid>
      <ProgressContainer>
        <FillerContainer>
          <BackgroundBar />
          {!fullWidth && <FillerBackground percentage={percentage} />}
          <Filler percentage={percentage} fullWidth={fullWidth} />
        </FillerContainer>
        {showPercentage && <PercentageText>{percentage?.toFixed(0)}%</PercentageText>}
      </ProgressContainer>
    </Container>
  );
}

const PercentageText = styled('span')`
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  margin-left: 0.5rem;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  column-gap: 0.5rem;
  row-gap: 0.3rem;
  grid-template-areas:
    'icon filename'
    '. status';
  margin-top: 0.3rem;
  margin-bottom: 0.3rem;
`;

const StatusIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-area: icon;
`;

const Status = styled('span', {
  shouldForwardProp: (prop) => prop !== 'largeText',
})<{ largeText: boolean }>`
  ${({ largeText }) => (largeText ? 'font-size: 0.9rem;' : 'font-size: 0.6rem;')}
  grid-area: status;
`;

const Container = styled('div')<{ fullWidth: boolean }>`
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const shouldForwardProp = (prop: string) => !['isError', 'largeText'].includes(prop);

const FileName = styled('div', { shouldForwardProp })<{ isError?: boolean; largeText: boolean }>`
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  grid-area: filename;
  span {
    ${({ largeText }) => (largeText ? 'font-size: 1.2rem;' : 'font-size: 0.9rem;')}
    font-weight: bold;
    color: ${({ isError }) => (isError ? /* istanbul ignore next */ 'red' : 'black')};
  }
`;

const BackgroundBar = styled('div')`
  position: absolute;
  height: 2px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.grey[800]};
  margin: 0.5rem 0;
  display: flex;
  align-items: center;
  box-shadow: 0px 0px 3px 0px ${({ theme }) => theme.palette.secondary.main};
  filter: brightness(0.8);
`;

const Filler = styled('div')<{ percentage: number; fullWidth: boolean }>`
  height: 2px;
  width: ${({ percentage }) => percentage}%;
  ${({ fullWidth, theme }) =>
    fullWidth
      ? `background: linear-gradient(to left, white, ${theme.palette.secondary.main} 20%)`
      : `background-color: ${theme.palette.secondary.main}`};
  border-radius: inherit;
  position: absolute;
  filter: none;
`;

const FillerBackground = styled('div')<{ percentage: number }>`
  height: 16px;
  width: ${({ percentage }) => percentage}%;
  background-color: white;
  position: absolute;
  filter: none;
`;

const FillerContainer = styled('div')`
  color: red;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  margin: 0.6rem 0;
`;

const ProgressContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;
